<template>
    <div>
        <div class="header-container">
            <h1 class="header-title">Privacy Policy</h1>
        </div>
        <div class="content-container">
            <p><strong>Last Updated:</strong> 1st Jan 2025</p>
            <p>
                <strong>Win2 Food Marketing Sdn. Bhd</strong>. ("we," "us," "our") is committed to protecting your privacy and ensuring
                your personal data is handled securely and in compliance with applicable laws. This Privacy Policy
                explains how we collect, use, and safeguard your information, particularly when interacting with us
                through our e-invoice digital forms.
            </p>

            <section>
                <h3>1. WHO ARE WE</h3>
                <p>
                    When this Privacy Policy refers to "we" or "us," it means Win2 Food Marketing Sdn. Bhd. and its
                    related entities.
                </p>
            </section>

            <section>
                <h3>2. WHAT TYPE OF INFORMATION DO WE COLLECT?</h3>
                <ul>
                    <li><strong>Personal Identification Information:</strong> Your name, personal tax TIN ID,
                        identification number, email address, mobile phone number, address, and postal code.</li>
                    <li><strong>Company Information:</strong> Your company name, company registration number, company
                        tax TIN number, SST number, email address, and phone number.</li>
                    <li><strong>Interaction Details:</strong> Information about your interactions with us, including any
                        complaints or comments you made, details of purchases, and how and when you contact us.</li>
                    <li><strong>Shopping Preferences:</strong> Information about your preferences, such as preferred
                        stores or products.</li>
                    <li><strong>Technical Information:</strong> Details of your visits to our websites, including IP
                        address, browser type, and pages visited.</li>
                    <li><strong>Cookies and Similar Technologies:</strong> Information gathered by the use of cookies to
                        enhance your experience on our website.</li>
                    <li><strong>Third-Party Information:</strong> Information received from affiliates, referral
                        programs, marketing service providers, or publicly available sources.</li>
                </ul>
            </section>

            <section>
                <h3>3. HOW AND WHY DO WE USE YOUR PERSONAL DATA?</h3>
                <ul>
                    <li><strong>Comply with Legal Obligations:</strong> To fulfill regulatory requirements, including
                        LHDN e-invoice compliance.</li>
                    <li><strong>Provide and Improve Our Services:</strong> To manage your account, process requests, and
                        enhance your experience with our services.</li>
                    <li><strong>Personalization:</strong> To tailor promotions, services, and products to your
                        preferences.</li>
                    <li><strong>Communication:</strong> To respond to your inquiries, feedback, or complaints.</li>
                    <li><strong>Fraud Prevention and Security:</strong> To safeguard our customers, employees, and
                        business from illegal activities.</li>
                </ul>
            </section>

            <section>
                <h3>4. HOW DO WE PROTECT YOUR PERSONAL DATA?</h3>
                <p>
                    We prioritize the security of your personal data by implementing robust technical and organizational
                    measures to prevent unauthorized access, disclosure, or alteration of your information. These
                    measures include encryption, access control, and regular security audits.
                </p>
            </section>

            <section>
                <h3>5. SHARING YOUR PERSONAL DATA</h3>
                <p>We may share your personal data with trusted third parties under the following conditions:</p>
                <ul>
                    <li><strong>Regulatory Compliance</strong>
                        <ul>
                            <li>Information may be disclosed to Malaysian regulatory bodies, such as:
                                <ul>
                                    <li>Lembaga Hasil Dalam Negeri Malaysia (LHDN).</li>
                                    <li>Royal Malaysian Customs Department.</li>
                                </ul>
                            </li>
                            <li>Purpose: To fulfill tax, e-invoicing, and SST compliance obligations.</li>
                        </ul>
                    </li>
                    <li><strong>Third-Party Service Providers</strong>
                        <ul>
                            <li>We collaborate with service providers to support business operations, such as:
                                <ul>
                                    <li>Payment Processors: To handle secure payments and refunds.</li>
                                    <li>Cloud Storage Providers: For safe data storage within Malaysia or countries with
                                        equivalent data protection laws.</li>
                                    <li>IT Support and Cybersecurity Firms: To ensure system and data security.</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li><strong>Marketing and Advertising Partners</strong>
                        <ul>
                            <li>With your explicit consent, your data may be shared with marketing agencies to:
                                <ul>
                                    <li>Deliver personalized advertisements.</li>
                                    <li>Offer promotional deals relevant to your preferences.</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li><strong>Affiliates or Business Partners</strong>
                        <ul>
                            <li>Data may be shared with affiliated companies or joint venture partners for:
                                <ul>
                                    <li>Operational purposes, such as shared systems or customer support.</li>
                                    <li>In compliance with Malaysia’s Personal Data Protection Act (PDPA).</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li><strong>Legal and Risk Management</strong>
                        <ul>
                            <li>Personal data may be disclosed to:
                                <ul>
                                    <li>Legal counsel, auditors, or insurance companies.</li>
                                </ul>
                            </li>
                            <li>Purpose: For claims, disputes, or risk assessments.</li>
                        </ul>
                    </li>
                </ul>
                <p>We ensure that all third parties handling your personal data adhere to strict confidentiality and
                    security standards as required by the PDPA and other relevant Malaysian laws.</p>
            </section>

            <section>
                <h3>6. DATA RETENTION</h3>
                <p>Whenever we collect or process your personal data, we will only keep it for as long as is necessary
                    for the purpose for which it was collected or to comply with legal and regulatory requirements. For
                    tax and compliance purposes, we may retain your data for a minimum of seven (7) years. At the end of
                    that retention period, your data will either be deleted completely or anonymized, for example by
                    aggregation with other data so that it can be used in a non-identifiable way for statistical
                    analysis and business planning.</p>
                <p>Key retention details include:</p>
                <ul>
                    <li><strong>Personal Identification Information:</strong> Retained for 7 years after the end of our
                        business relationship to comply with Malaysian taxation and accounting laws.</li>
                    <li><strong>Company Information:</strong> Retained for 7 years to meet statutory obligations under
                        the Companies Act 2016 and related regulations.</li>
                    <li><strong>Transaction and Interaction Details:</strong> Retained for 7 years from the date of the
                        transaction for audit and regulatory purposes.</li>
                    <li><strong>Cookies and Technical Information:</strong>
                        <ul>
                            <li>Session Cookies: Retained only for the duration of your browsing session.</li>
                            <li>Analytics Data: Retained for up to 2 years.</li>
                        </ul>
                    </li>
                </ul>
                <p>After these periods, your personal data will be securely managed, ensuring privacy and compliance
                    with applicable laws.</p>
            </section>

            <section>
                <h3>7. YOUR RIGHTS</h3>
                <p>You have the right to:</p>
                <ul>
                    <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                    <li><strong>Rectification:</strong> Request correction of any inaccurate or incomplete data.</li>
                    <li><strong>Erasure:</strong> Request deletion of your personal data when it is no longer necessary
                        for the purposes for which it was collected.</li>
                    <li><strong>Restriction:</strong> Request restriction of processing under certain circumstances.
                    </li>
                    <li><strong>Data Portability:</strong> Request transfer of your data to another party.</li>
                    <li><strong>Object:</strong> Object to the processing of your personal data under certain
                        conditions.</li>
                </ul>
                <p>To exercise any of these rights, please email us at <a
                        :href="`mailto:einvoice@win2food.com.my`">einvoice@win2food.com.my</a>.</p>
            </section>

            <section>
                <h3>8. COOKIES AND TRACKING TECHNOLOGIES</h3>
                <p>
                    We use cookies and similar technologies to enhance your experience on our website. You can manage
                    your cookie preferences through your browser settings.
                </p>
            </section>

            <section>
                <h3>9. CHANGES TO THIS PRIVACY POLICY</h3>
                <p>
                    We may update this Privacy Policy from time to time. Any changes will be posted on this page, and
                    where appropriate, notified to you by email.
                </p>
            </section>

            <section>
                <h3>10. CONTACT US</h3>
                <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
                <ul>
                    <li style="margin-bottom: 0.5rem;"><strong>Email:</strong> <a
                            :href="`mailto:einvoice@win2food.com.my`">einvoice@win2food.com.my</a>
                    </li>
                    <li style="margin-bottom: 0.5rem;"><strong>Postal Address:</strong> No.6, Jalan Tahana, Kawasan
                        Perindustrian Tampoi, 81200 Johor
                        Bahru, Johor, Malaysia.</li>
                    <li style="margin-bottom: 0.5rem;"><strong>Phone:</strong> +607-235 1103 (Hunting Line)</li>
                </ul>
                <p>By providing your information to us, you agree to the terms outlined in this Privacy Policy.</p>
            </section>
        </div>
    </div>
</template>

<script>
export default {
    name: "PrivacyPolicy",
};
</script>

<style scoped>
.header-container {
    text-align: center;
    background-color: #3663e6;
    padding: 20px;
    max-width: 1200px;
    margin: 3rem auto 0 auto;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
}

.header-title {
    color: white;
}

.content-container {
    background: white;
    padding: 20px 50px;
    max-width: 1200px;
    margin: 0rem auto 3rem auto;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border-bottom-left-radius: 1rem;
    border-bottom-right-radius: 1rem;
}

h1,
h3 {
    margin: 1rem 0;
    color: black;
}

ul {
    margin: 0;
    padding: 0 0 0 20px;
}

li {
    margin-bottom: 1rem;
}

a {
    color: #007bff;
    text-decoration: none;
}

a:hover {
    text-decoration: underline;
}

p {
    text-align: justify;
}

@media (max-width: 575.99px) {
    .header-container {
        margin: 1.5rem 2rem 0 2rem;
    }

    .content-container {
        font-size: 0.875rem;
        margin: 0rem 2rem 1.5rem 2rem;
        padding: 20px 30px;
    }

    h1 {
        font-size: 2.5rem;
    }

    li {
        margin-bottom: 0.75rem;
    }
}
</style>